import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, MapPin, DoorClosed, Paperclip, Check, X, CalendarPlus, Loader2, Download, Building2 } from "lucide-react";
import { inspectionRequestsApi, usersApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDateTime, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TABS = [
  { v: "pending", label: "Pending" },
  { v: "accepted", label: "Accepted" },
  { v: "declined", label: "Declined" },
  { v: "", label: "All" },
];

function statusTone(s) {
  return { pending: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", declined: "bg-slate-200 text-slate-600" }[s] || "bg-slate-100 text-slate-600";
}

export default function InspectionRequests() {
  const [tab, setTab] = useState("pending");
  const { data, loading, error, reload } = useAsync(() => inspectionRequestsApi.list(tab || undefined), [tab]);
  const [scheduleFor, setScheduleFor] = useState(null); // request being scheduled
  const list = Array.isArray(data) ? data : [];

  const downloadAttachment = async (req, idx) => {
    try {
      const r = await inspectionRequestsApi.attachment(req.id, idx);
      const { filename, content } = r.data || {};
      const a = document.createElement("a");
      a.href = `data:application/octet-stream;base64,${content}`;
      a.download = filename || "report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(apiError(e, "Could not download attachment")); }
  };

  const resolve = async (req, status, createBuilding) => {
    try {
      await inspectionRequestsApi.resolve(req.id, { status, create_building: !!createBuilding });
      toast.success(status === "accepted" ? "Request accepted" : "Request declined");
      reload();
    } catch (e) { toast.error(apiError(e, "Could not update request")); }
  };

  return (
    <div>
      <PageHeader title="Inspection requests" subtitle="Survey requests submitted by your clients" testId="inspection-requests-header" />

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`ir-tab-${t.v || "all"}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === t.v ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <LoadingState label="Loading requests…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && list.length === 0 && (
        <EmptyState icon={Inbox} title="No requests" message="Client inspection requests will appear here." />
      )}

      {!loading && !error && list.length > 0 && (
        <div className="space-y-3">
          {list.map((req) => (
            <Card key={req.id} className="p-5" data-testid={`ir-card-${req.id}`}>
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900">{req.property_name || "New survey request"}</h3>
                  <p className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {[req.address, req.postcode].filter(Boolean).join(", ")}</p>
                </div>
                <Badge className={statusTone(req.status)}>{req.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                {req.client_name ? <span>Client: <span className="font-medium text-slate-800">{req.client_name}</span></span> : null}
                {req.approx_door_count != null ? <span className="flex items-center gap-1"><DoorClosed className="h-3.5 w-3.5" /> ~{req.approx_door_count} doors</span> : null}
                {(req.door_types || []).length ? <span>Types: {req.door_types.join(", ")}</span> : null}
                <span className="text-slate-400">Submitted {fmtDateTime(req.created_at)}</span>
              </div>
              {req.notes ? <p className="mt-2 rounded-lg bg-slate-50 p-2.5 text-sm text-slate-600">{req.notes}</p> : null}
              {(req.access_details || req.parking_details) && (
                <div className="mt-2 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  {req.access_details ? <div><span className="text-slate-400">Access:</span> {req.access_details}</div> : null}
                  {req.parking_details ? <div><span className="text-slate-400">Parking:</span> {req.parking_details}</div> : null}
                </div>
              )}
              {(req.previous_reports || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {req.previous_reports.map((r, i) => (
                    <Button key={i} variant="outline" size="sm" onClick={() => downloadAttachment(req, i)} data-testid={`ir-attach-${req.id}-${i}`}>
                      <Paperclip className="mr-1.5 h-3.5 w-3.5" /> {r.filename || `Report ${i + 1}`} <Download className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  ))}
                </div>
              )}

              {req.status === "pending" && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <Button size="sm" onClick={() => setScheduleFor(req)} data-testid={`ir-schedule-${req.id}`}>
                    <CalendarPlus className="mr-1.5 h-4 w-4" /> Accept &amp; schedule
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(req, "accepted", true)} data-testid={`ir-accept-${req.id}`}>
                    <Check className="mr-1.5 h-4 w-4" /> Accept &amp; create building
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => resolve(req, "declined")} data-testid={`ir-decline-${req.id}`}>
                    <X className="mr-1.5 h-4 w-4" /> Decline
                  </Button>
                </div>
              )}
              {req.status === "accepted" && req.created_building_id && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <BuildingLink buildingId={req.created_building_id} scheduledId={req.scheduled_inspection_id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <ScheduleDialog request={scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)} onDone={() => { setScheduleFor(null); reload(); }} />
    </div>
  );
}

function BuildingLink({ buildingId, scheduledId }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={() => navigate(`/buildings/${buildingId}`)} data-testid="ir-view-building">
        <Building2 className="mr-1.5 h-4 w-4" /> View building
      </Button>
      {scheduledId ? <Button size="sm" variant="ghost" onClick={() => navigate("/schedule")}>View in schedule</Button> : null}
    </div>
  );
}

function ScheduleDialog({ request, onOpenChange, onDone }) {
  const open = !!request;
  const users = useAsync(() => (open ? usersApi.list() : Promise.resolve({ data: [] })), [open]);
  const [inspectorId, setInspectorId] = useState("");
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const inspectors = useMemo(() => {
    const arr = Array.isArray(users.data) ? users.data : [];
    return arr.filter((u) => u.role === "inspector" || u.role === "admin" || u.is_company_admin || (u.secondary_roles || []).includes("inspector"));
  }, [users.data]);

  const submit = async () => {
    if (!inspectorId) { toast.error("Pick an inspector"); return; }
    if (!when) { toast.error("Pick a date & time"); return; }
    setBusy(true);
    try {
      await inspectionRequestsApi.convertToScheduled(request.id, {
        inspector_id: inspectorId,
        scheduled_for: new Date(when).toISOString(),
        notes: notes.trim() || undefined,
      });
      toast.success("Scheduled — building created and added to the calendar");
      setInspectorId(""); setWhen(""); setNotes("");
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not schedule")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ir-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Accept &amp; schedule survey</DialogTitle>
          <DialogDescription>{request?.property_name} — creates the building and books the inspector in one step.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Inspector</Label>
            <Select value={inspectorId} onValueChange={setInspectorId}>
              <SelectTrigger className="mt-1" data-testid="ir-inspector-select"><SelectValue placeholder="Choose inspector" /></SelectTrigger>
              <SelectContent>
                {inspectors.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date &amp; time</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1" data-testid="ir-schedule-when" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="ir-schedule-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ir-schedule-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarPlus className="mr-2 h-4 w-4" />} Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
